import { useState } from 'react';
import type { ToolPart, QuestionItem } from '../../types';
import { useChatActions } from "../ChatActionsContext";

interface Props {
  part: ToolPart;
}

/**
 * Renders the `question` AG-UI tool. The toolspec (name, description,
 * parameters) lives on the backend in `frontend_tools.py` and is
 * fetched at runtime via `frontendToolsQueryOptions` — that's the same
 * catalog headless callers (webhook fires, cron routines) register
 * against, so the LLM sees identical wording everywhere.
 */
export function QuestionCard({ part }: Props) {
  const { sendToolResult } = useChatActions();
  const questions = (part.state.input?.questions as QuestionItem[] | undefined) || [];
  const [tab, setTab] = useState(0);
  const [answers, setAnswers] = useState<string[][]>(() => questions.map(() => []));
  const [customTexts, setCustomTexts] = useState<string[]>(() => questions.map(() => ''));
  const [submitting, setSubmitting] = useState(false);

  // ── Helpers ──

  const answered = (i: number) => (answers[i]?.length ?? 0) > 0 || !!customTexts[i]?.trim();
  const allAnswered = questions.every((_, i) => answered(i));

  // ── Completed state ──

  if (part.state.status === 'completed' || part.state.status === 'error') {
    return (
      <div className="tool-step tool-completed">
        <div className="tool-step-rail">
          <div className={`tool-step-dot ${part.state.status === 'error' ? 'tool-dot-error' : 'tool-dot-done'}`} />
        </div>
        <div className="tool-step-body">
          <div className="tool-step-label">Уточнение — отвечено</div>
          {(part.state.output || part.state.error) && (
            <div className="question-preview">{part.state.output || part.state.error}</div>
          )}
        </div>
      </div>
    );
  }

  // ── Loading state ──

  if (questions.length === 0) {
    return (
      <div className="msg-question">
        <div className="question-header">
          <div className="question-header-text">
            <div className="question-title">Загрузка вопроса…</div>
          </div>
        </div>
      </div>
    );
  }

  // ── Active state ──

  const q = questions[tab]!;
  const isMulti = !!q?.multiple;
  const isSingle = questions.length === 1;

  const toggleOption = (label: string) => {
    setAnswers(prev => {
      const next = [...prev];
      const current = [...(next[tab] ?? [])];

      if (isMulti) {
        const idx = current.indexOf(label);
        if (idx >= 0) current.splice(idx, 1);
        else current.push(label);
        next[tab] = current;
      } else {
        next[tab] = current[0] === label ? [] : [label];
      }
      return next;
    });
  };

  const sendAnswer = async (finalAnswers: string[][]) => {
    setSubmitting(true);
    try {
      let content: string;
      if (finalAnswers.length === 0) {
        content = 'Пользователь пропустил вопрос.';
      } else {
        const lines = questions.map((q, i) => {
          const values = finalAnswers[i] ?? [];
          const joined = values.length ? values.join(', ') : 'не отвечено';
          return `• ${q.question} — ${joined}`;
        });
        content = `Ответы пользователя:\n${lines.join('\n')}`;
      }
      await sendToolResult(part.id, content);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    const finalAnswers = questions.map((_, i) => {
      const selected = answers[i] ?? [];
      const custom = customTexts[i]?.trim();
      if (custom && !selected.includes(custom)) return [...selected, custom];
      return selected.length > 0 ? selected : ['Unanswered'];
    });
    sendAnswer(finalAnswers);
  };

  // ── Render ──

  return (
    <div className="msg-question">
      <div className="question-header">
        <div className="question-header-text">
          {!isSingle && (
            <div className="question-tabs">
              {questions.map((qi, i) => (
                <button
                  key={i}
                  className={`question-tab${i === tab ? ' active' : ''}${answered(i) && i !== tab ? ' answered' : ''}`}
                  onClick={() => setTab(i)}
                >
                  {qi.header}
                </button>
              ))}
            </div>
          )}
          <div className="question-title">
            {q.question}
            {isMulti && <span className="question-hint">Можно выбрать несколько</span>}
          </div>
        </div>
        <button className="question-dismiss-x" onClick={() => sendAnswer([])} disabled={submitting} title="Пропустить">×</button>
      </div>

      <div className="question-options">
        {q.options.map((opt, i) => {
          const selected = (answers[tab] ?? []).includes(opt.label);
          return (
            <button
              key={i}
              className={`question-option${selected ? ' selected' : ''}${isMulti ? ' multiple' : ''}`}
              onClick={() => toggleOption(opt.label)}
            >
              <span className="question-option-check" />
              <div>
                <div className="question-option-label">{opt.label}</div>
                {opt.description && <div className="question-option-desc">{opt.description}</div>}
              </div>
            </button>
          );
        })}
      </div>

      <input
        type="text"
        className="question-custom"
        placeholder="Свой вариант"
        value={customTexts[tab] ?? ''}
        onChange={e => {
          const val = e.target.value;
          setCustomTexts(prev => { const next = [...prev]; next[tab] = val; return next; });
        }}
        onKeyDown={e => { if (e.key === 'Enter' && allAnswered) handleSubmit(); }}
      />

      <div className="question-footer">
        {!isSingle && (
          <div className="question-nav">
            <button
              className="btn-question-nav"
              onClick={() => setTab(t => t - 1)}
              disabled={tab === 0}
            >
              Назад
            </button>
            <button
              className="btn-question-nav"
              onClick={() => setTab(t => t + 1)}
              disabled={tab === questions.length - 1}
            >
              Далее
            </button>
          </div>
        )}
        <button
          className="btn-question-submit"
          onClick={handleSubmit}
          disabled={!allAnswered || submitting}
        >
          Отправить
        </button>
      </div>
    </div>
  );
}
